<template>
    <modal :modal-id="modalId" :title="modalTitle" :preloader="preloader" @submit="submit" @close-modal="closeModal">
        <template slot="body">
            <app-overlay-loader v-if="preloader" />
            <form class="mb-0" :class="{ 'loading-opacity': preloader }" ref="form"
                :data-url='selectedUrl ? `users/${inputs.id}` : `users`'>
                
                <!-- First Name Input -->
                <div class="form-group">
                    <label for="inputs_firstname">{{ $t('first_name') }}</label>
                    <app-input id="inputs_firstname" type="text" v-model="inputs.first_name"
                        :placeholder="$t('first_name')" :required="true" />
                </div>
                
                <!-- Last Name Input -->
                <div class="form-group">
                    <label for="inputs_lastname">{{ $t('last_name') }}</label>
                    <app-input id="inputs_lastname" type="text" v-model="inputs.last_name"
                        :placeholder="$t('last_name')" :required="true" />
                </div>

                <!-- Email Address Input -->
                <div class="form-group">
                    <label for="inputs_email">{{ $t('email') }}</label>
                    <app-input id="inputs_email" type="email" v-model="inputs.email"
                        :placeholder="$t('email')" :required="true" />
                </div>

                <template v-if="!selectedUrl">
                    <div class="form-group">
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <label for="inputs_password" class="mb-0">{{ $t('password') }}</label>
                            <button type="button" class="btn btn-sm btn-outline-secondary" @click="generatePassword">
                                {{ $t('generate_password') }}
                            </button>
                        </div>
                        <app-input id="inputs_password" type="password" v-model="inputs.password"
                            :placeholder="$t('password')" :required="true" :specialValidation="true"
                            :show-password="true" autocomplete="new-password" />
                        <small class="text-muted d-inline-block mt-2">
                            {{ $t('password_generator_hint') }}
                        </small>
                    </div>

                    <div class="form-group">
                        <label for="inputs_password_confirmation">{{ $t('confirm_password') }}</label>
                        <app-input id="inputs_password_confirmation" type="password" v-model="inputs.password_confirmation"
                            :placeholder="$t('confirm_password')" :required="true" same-as="inputs_password"
                            :show-password="true" autocomplete="new-password" />
                    </div>
                </template>

                <!-- User Status Input -->
                <div class="form-group">
                    <label for="inputs_status">{{ $t('trip_status') }}</label>
                    <app-input id="inputs_status" list-value-field="translated_name" type="select" 
                        :required="true" v-model="inputs.status_id" :list="statusList" />
                </div>
                <!-- Role Select Input -->
                <div class="form-group" v-if="!this.selectedUrl">
                    <label for="role">{{ $t('role') }}</label>
                    <div class="d-flex align-items-center w-100">
                        <app-input class="w-100" id="role" v-model="inputs.role_id"
                            :list="roleList" list-value-field="name" :placeholder="$t('choose_a_role')"
                            type="search-select" :required="true"/>
                    </div>
                </div>

                <div class="form-group">
                    <label for="authorized-clients">{{ $t('authorized_clients') }}</label>
                    <app-input
                        id="authorized-clients"
                        type="multi-select"
                        v-model="inputs.client_ids"
                        :list="clientList"
                        list-value-field="name"
                        :placeholder="$t('clients')"
                    />
                    <small class="text-muted d-inline-block mt-2">
                        {{ $t('client_scope_hint') }}
                    </small>
                </div>
            </form>
        </template>
    </modal>
</template>

<script>
import { mapState } from 'vuex';
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import { ModalMixin } from '../../../Mixins/ModalMixin';
import * as actions from '../../../Config/ApiUrl';


export default {
    name: "UserAddEditModal",
    mixins: [FormMixin, ModalMixin],
    props: {
        tableId: String,
    },
    data() {
        return {
            preloader: false,
            inputs: {
                client_ids: [],
            },
            modalId: 'user-add-edit-Modal',
            modalTitle: `${this.$t('add')} ${this.$t('new')} ${this.$t('user')}`,
            statusList: [],
            clientList: [],
        }
    },
    computed: {
        ...mapState({
            roleList: state => state.notificationSettings.roles,
        }),
    },
    created() {
        this.getStatuses();
        this.getClients();
        this.$store.dispatch('getAllRoles');
        if (this.selectedUrl) {
            this.modalTitle = this.$t('edit');
            this.preloader = true;
        }
    },
    methods: {
        generatePassword() {
            const password = this.buildStrongPassword();

            this.inputs.password = password;
            this.inputs.password_confirmation = password;
        },
        submit() {
            this.save(this.inputs);
            this.$hub.$emit('reload-' + this.tableId);
        },
        getStatuses() {
            let url = `${actions.GET_STATUSES}?type=user`;
            this.axiosGet(url).then(response => {
                this.statusList = response.data;
            });
        },
        getClients() {
            this.axiosGet('/support/clients').then(response => {
                this.clientList = response.data || [];
            });
        },
        afterSuccess(response) {
            this.$toastr.s(response.data.message);
            this.$hub.$emit('reload-' + this.tableId);
        },

        afterSuccessFromGetEditData(response) {
            this.inputs = {
                ...response.data,
                client_ids: Array.isArray(response.data.client_ids) ? response.data.client_ids : [],
            };
            this.preloader = false;
        },
        buildStrongPassword(length = 12) {
            const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
            const lowercase = 'abcdefghijkmnopqrstuvwxyz';
            const numbers = '23456789';
            const symbols = '!@#$%*_-';
            const all = `${uppercase}${lowercase}${numbers}${symbols}`;

            const pick = (characters) => {
                if (window.crypto && window.crypto.getRandomValues) {
                    const buffer = new Uint32Array(1);
                    window.crypto.getRandomValues(buffer);
                    return characters[buffer[0] % characters.length];
                }

                return characters[Math.floor(Math.random() * characters.length)];
            };

            const password = [
                pick(uppercase),
                pick(lowercase),
                pick(numbers),
                pick(symbols),
            ];

            while (password.length < length) {
                password.push(pick(all));
            }

            return password
                .sort(() => Math.random() - 0.5)
                .join('');
        },
    },
}
</script>
